import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AppLayout } from "./components/layout/AppLayout";
import { ApiKeyProvider } from "./contexts/ApiKeyContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProviderKeyProvider } from "./contexts/ProviderKeyContext";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import LandingPage from "./pages/LandingPage";

// Font links
const FontLinks = () => {
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Fragment+Mono&family=Manrope:wght@300..800&family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
    </>
  );
};

// Lazy load all pages for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Providers = lazy(() => import("./pages/Providers"));
const ProviderApiKeys = lazy(() => import("./pages/ProviderApiKeys"));
const Integrations = lazy(() => import("./pages/Integrations"));
const ApiIntegration = lazy(() => import("./pages/ApiIntegration"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const Team = lazy(() => import("./pages/Team"));
const Invitations = lazy(() => import("./pages/Invitations"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const ProviderConfigs = lazy(() => import("./pages/ProviderConfigs"));
const Playground = lazy(() => import("./pages/Playground"));
const Datasets = lazy(() => import("./pages/Datasets"));
const FineTuning = lazy(() => import("./pages/FineTuning"));
const LangChain = lazy(() => import("./pages/LangChain"));
const Usage = lazy(() => import("./pages/Usage"));
const LangGraph = lazy(() => import("./pages/LangGraph"));
const Agents = lazy(() => import("./pages/Agents"));
const RAG = lazy(() => import("./pages/RAG"));
const ModelSubscriptions = lazy(() => import("./pages/ModelSubscriptions"));
const Billing = lazy(() => import("./pages/Billing"));
const Consumption = lazy(() => import("./pages/Consumption"));
const ABTesting = lazy(() => import("./pages/ABTesting"));
const CostOptimization = lazy(() => import("./pages/CostOptimization"));
const Tracing = lazy(() => import("./pages/Tracing"));
const Guardrails = lazy(() => import("./pages/Guardrails"));
const Notifications = lazy(() => import("./pages/Notifications"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const SLADashboard = lazy(() => import("./pages/SLADashboard"));
const Organizations = lazy(() => import("./pages/Organizations"));
// Documentation Pages
const PlatformOverview = lazy(() => import("./pages/docs/platform-overview"));
const DocsIndex = lazy(() => import("./pages/docs/index"));
const Introduction = lazy(() => import("./pages/docs/get-started/introduction"));
const Installation = lazy(() => import("./pages/docs/get-started/installation"));
const CreatingClient = lazy(() => import("./pages/docs/get-started/creating-a-client"));
const Quickstart = lazy(() => import("./pages/docs/get-started/quickstart"));
const FAQs = lazy(() => import("./pages/docs/get-started/faqs"));
const ModelsOverview = lazy(() => import("./pages/docs/models/overview"));
const OpenAIModels = lazy(() => import("./pages/docs/models/openai"));
const AzureOpenAIModels = lazy(() => import("./pages/docs/models/azure-openai"));
const AnthropicModels = lazy(() => import("./pages/docs/models/anthropic"));
const GeminiModels = lazy(() => import("./pages/docs/models/gemini"));
const MistralModels = lazy(() => import("./pages/docs/models/mistral"));
const TextGenIntro = lazy(() => import("./pages/docs/text-generation/introduction"));
const CompletionAPI = lazy(() => import("./pages/docs/text-generation/completion-api"));
const StreamingResponses = lazy(() => import("./pages/docs/text-generation/streaming"));
const StructuredOutputs = lazy(() => import("./pages/docs/text-generation/structured-outputs"));
const GenerationParams = lazy(() => import("./pages/docs/text-generation/parameters"));
const PromptEngineering = lazy(() => import("./pages/docs/text-generation/prompt-engineering"));
const EmbeddingsIntro = lazy(() => import("./pages/docs/embeddings/introduction"));
const SemanticSearch = lazy(() => import("./pages/docs/embeddings/semantic-search"));
const Reranking = lazy(() => import("./pages/docs/embeddings/reranking"));
const RAGIntro = lazy(() => import("./pages/docs/rag/introduction"));
const BuildingRAG = lazy(() => import("./pages/docs/rag/building-rag"));
const OptimizingRAG = lazy(() => import("./pages/docs/rag/optimizing-rag"));
const APIKeys = lazy(() => import("./pages/docs/production/api-keys"));
const Monitoring = lazy(() => import("./pages/docs/production/monitoring"));
const Security = lazy(() => import("./pages/docs/production/security"));
const CostManagement = lazy(() => import("./pages/docs/production/cost-management"));
const AIFeaturesIntro = lazy(() => import("./pages/docs/ai-features/introduction"));
const LangChainDocs = lazy(() => import("./pages/docs/ai-features/langchain"));
const LangGraphDocs = lazy(() => import("./pages/docs/ai-features/langgraph"));
const AgentsDocs = lazy(() => import("./pages/docs/ai-features/agents"));

// Loading component
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="space-y-4 text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
      <p className="text-muted-foreground font-space">Loading...</p>
    </div>
  </div>
);

// Protected route component
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Redirect to login, but save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

// Admin-only route component
function AdminRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'admin') {
    // Redirect non-admin users to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

// Subdomain detector component 
function SubdomainHandler() {
  const { isAuthenticated, isLoading } = useAuth();
  const [isApiSubdomain, setIsApiSubdomain] = useState(false);
  
  useEffect(() => {
    // Check if we're on the API subdomain
    const hostname = window.location.hostname;
    setIsApiSubdomain(hostname.startsWith('api.'));
  }, []);
  
  if (isLoading) {
    return <PageLoader />;
  }
  
  // If on API subdomain and not authenticated, redirect to login
  if (isApiSubdomain && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If on API subdomain and authenticated, go to dashboard
  if (isApiSubdomain && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // For www subdomain: If authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Otherwise, always show the landing page without any redirection
  return <LandingPage />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <FontLinks />
    <ThemeProvider>
      <TooltipProvider>
        <AuthProvider>
          <ApiKeyProvider>
            <ProviderKeyProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Home route - uses subdomain handler to route appropriately */}
                  <Route path="/" element={<SubdomainHandler />} />
                  
                  {/* Public Routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
                  <Route path="/landing" element={<LandingPage />} />
                  
                  {/* Documentation Routes - Publicly accessible */}
                  <Route path="/docs" element={<DocsIndex />} />
                  <Route path="/docs/platform-overview" element={<PlatformOverview />} />
                  
                  {/* Get Started Routes */}
                  <Route path="/docs/get-started/introduction" element={<Introduction />} />
                  <Route path="/docs/get-started/installation" element={<Installation />} />
                  <Route path="/docs/get-started/creating-a-client" element={<CreatingClient />} />
                  <Route path="/docs/get-started/quickstart" element={<Quickstart />} />
                  <Route path="/docs/get-started/faqs" element={<FAQs />} />
                  
                  {/* Models Routes */}
                  <Route path="/docs/models/overview" element={<ModelsOverview />} />
                  <Route path="/docs/models/openai" element={<OpenAIModels />} />
                  <Route path="/docs/models/azure-openai" element={<AzureOpenAIModels />} />
                  <Route path="/docs/models/anthropic" element={<AnthropicModels />} />
                  <Route path="/docs/models/gemini" element={<GeminiModels />} />
                  <Route path="/docs/models/mistral" element={<MistralModels />} />
                  
                  {/* Text Generation Routes */}
                  <Route path="/docs/text-generation/introduction" element={<TextGenIntro />} />
                  <Route path="/docs/text-generation/completion-api" element={<CompletionAPI />} />
                  <Route path="/docs/text-generation/streaming" element={<StreamingResponses />} />
                  <Route path="/docs/text-generation/structured-outputs" element={<StructuredOutputs />} />
                  <Route path="/docs/text-generation/parameters" element={<GenerationParams />} />
                  <Route path="/docs/text-generation/prompt-engineering" element={<PromptEngineering />} />
                  
                  {/* Embeddings Routes */}
                  <Route path="/docs/embeddings/introduction" element={<EmbeddingsIntro />} />
                  <Route path="/docs/embeddings/semantic-search" element={<SemanticSearch />} />
                  <Route path="/docs/embeddings/reranking" element={<Reranking />} />
                  
                  {/* RAG Routes */}
                  <Route path="/docs/rag/introduction" element={<RAGIntro />} />
                  <Route path="/docs/rag/building-rag" element={<BuildingRAG />} />
                  <Route path="/docs/rag/optimizing-rag" element={<OptimizingRAG />} />
                  
                  {/* AI Features Routes */}
                  <Route path="/docs/ai-features/introduction" element={<AIFeaturesIntro />} />
                  <Route path="/docs/ai-features/langchain" element={<LangChainDocs />} />
                  <Route path="/docs/ai-features/langgraph" element={<LangGraphDocs />} />
                  <Route path="/docs/ai-features/agents" element={<AgentsDocs />} />

                  {/* Production Routes */}
                  <Route path="/docs/production/api-keys" element={<APIKeys />} />
                  <Route path="/docs/production/monitoring" element={<Monitoring />} />
                  <Route path="/docs/production/security" element={<Security />} />
                  <Route path="/docs/production/cost-management" element={<CostManagement />} />

                  {/* Protected Routes - Available to all authenticated users */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<AppLayout />}>
                      {/* Dashboard & Core */}
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/playground" element={<Playground />} />

                      {/* API & Provider Management */}
                      <Route path="/api-keys" element={<ApiKeys />} />
                      <Route path="/api-integration" element={<ApiIntegration />} />
                      <Route path="/providers" element={<Providers />} />
                      <Route path="/provider-api-keys" element={<ProviderApiKeys />} />
                      <Route path="/model-subscriptions" element={<ModelSubscriptions />} />

                      {/* AI Features */}
                      <Route path="/langchain" element={<LangChain />} />
                      <Route path="/langgraph" element={<LangGraph />} />
                      <Route path="/agents" element={<Agents />} />
                      <Route path="/rag" element={<RAG />} />
                      <Route path="/datasets" element={<Datasets />} />
                      <Route path="/fine-tuning" element={<FineTuning />} />

                      {/* Analytics & Billing */}
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/consumption" element={<Consumption />} />
                      <Route path="/usage" element={<Usage />} />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/ab-testing" element={<ABTesting />} />
                      <Route path="/cost-optimization" element={<CostOptimization />} />

                      {/* Operations */}
                      <Route path="/tracing" element={<Tracing />} />
                      <Route path="/guardrails" element={<Guardrails />} />

                      {/* Integration Guide - Available to all users */}
                      <Route path="/integrations" element={<Integrations />} />

                      {/* Account */}
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/organizations" element={<Organizations />} />

                      {/* User Settings - Available to all users */}
                      <Route path="/settings" element={<Settings />} />
                    </Route>
                  </Route>

                  {/* Admin-Only Routes */}
                  <Route element={<AdminRoute />}>
                    <Route element={<AppLayout />}>
                      <Route path="/team" element={<Team />} />
                      <Route path="/invitations" element={<Invitations />} />
                      <Route path="/admin/settings" element={<AdminSettings />} />
                      <Route path="/admin/audit-logs" element={<AuditLogs />} />
                      <Route path="/admin/sla" element={<SLADashboard />} />
                      <Route path="/admin/provider-configs" element={<ProviderConfigs />} />
                    </Route>
                  </Route>

                  {/* 404 Route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
            </ProviderKeyProvider>
          </ApiKeyProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
